import GlobalVariables from "./globalVariables";

/** Build a human display name for a loan-thin record. */
function getLoanDisplayName(loan) {
  const borrower = Array.isArray(loan?.borrowers) ? loan.borrowers[0] : null;
  const fromBorrower =
    borrower?.name ||
    `${borrower?.firstName || ""} ${borrower?.lastName || ""}`.trim();
  return fromBorrower || loan?.address || loan?.number || loan?.id || "Unnamed loan";
}

/** Normalize a loan-thin record into the shape the UI needs. */
function normalizeLoan(loan) {
  return {
    id: loan.id,
    name: getLoanDisplayName(loan),
    address: loan.address || "",
    amount: loan.amount ?? null,
    type: loan.type || "",
    stage: loan.stage || "",
    number: loan.number || "",
    hasDocuments: !!loan.hasDocuments,
  };
}

/**
 * Fetches a page of loans for a user's organization using the backend's
 * server-side search and cursor-based pagination, so orgs with more than one
 * page of loans can search and page through all of them (not just the first 50).
 *
 * @param {string} uid - User ID
 * @param {string} organizationId - Organization ID
 * @param {Object} [options]
 * @param {string} [options.search] - Server-side search query (borrower, address, loan ID, etc.)
 * @param {string|null} [options.cursor] - Pagination cursor from a previous page
 * @param {number} [options.pageSize] - Page size
 * @returns {Promise<{ loans: Array, cursor: (string|null) }>} Normalized loans and the next cursor
 */
async function listLoans(
  uid,
  organizationId,
  { search = "", cursor = null, pageSize = 6, signal } = {},
) {
  const base = GlobalVariables.getAPIURL();
  let url = `${base}/api/loan/loans-paginated?uid=${encodeURIComponent(uid)}&organizationId=${encodeURIComponent(organizationId)}&pageSize=${pageSize}`;
  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }
  if (cursor) {
    url += `&cursor=${encodeURIComponent(cursor)}`;
  }
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Loans fetch failed: ${res.status}`);
  }
  const data = await res.json();
  return {
    loans: (data.records || []).map(normalizeLoan),
    cursor: data.nextCursor || null,
  };
}

/** Normalize a document record into the shape the UI needs. */
function normalizeDocument(doc) {
  const name =
    doc.name || doc.originalName || doc.fileName || doc.title || "Document";
  return {
    id: doc.id || name,
    name,
    date: doc.createdAt || doc.uploadedAt || null,
    mimeType: doc.mimeType || doc.type || "",
    hasText: !!(doc.hasTextContent || doc.textContent),
    textContent: doc.textContent || "",
    // Grouping inputs, kept so the picker can organize documents into category
    // folders (mirrors the client's resolveDocumentGroup priority chain).
    group: doc.group || "",
    category: doc.category || "",
    classification: doc.classification ?? null,
    splits: doc.splits ?? null,
    extendAISplitResult: doc.extendAISplitResult ?? null,
    // Existing generated thumbnail used by the main Documents tab. The picker
    // only displays this URL; it does not generate previews or download PDFs.
    previewImageURL: doc.previewImageURL || "",
    downloadURL: doc.downloadURL || "",
  };
}

/**
 * Fetches all documents for a loan (normalized).
 * @param {string} loanId
 * @param {string} organizationId
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Abort signal to cancel a stale request
 * @returns {Promise<Array>} Normalized document objects
 */
async function getLoanDocuments(loanId, organizationId, { signal } = {}) {
  const base = GlobalVariables.getAPIURL();
  const url = `${base}/api/loan/documents?loanId=${encodeURIComponent(loanId)}&organizationId=${encodeURIComponent(organizationId)}`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Documents fetch failed: ${res.status}`);
  }
  const docs = await res.json();
  // Guard against soft-deleted/hidden docs even if the backend hasn't filtered
  // them, so tombstones never appear as selectable in the picker.
  return (docs || [])
    .filter((doc) => doc.deleted !== true && doc.hide !== true)
    .map(normalizeDocument);
}

const LoanDocs = { listLoans, getLoanDocuments, getLoanDisplayName };
export default LoanDocs;
